/**
 * UserPicker (P268.2) -- a reusable typeahead over the users table.
 *
 * Type >= 2 chars -> debounced `/v1/me/users-search` -> pick a row ->
 * `onSelect(user)`. The first real use is setting an assessment's
 * assignee / approver (the assignment affordance the workflow lacked).
 * Self-contained: holds its own query + the last-picked label so the
 * caller only has to persist the id.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { UserOption } from "shared/api/types";
import { useDebouncedValue } from "shared/hooks/useDebouncedValue";
import { useUsersSearch } from "shared/work/useMyWork";

export function UserPicker({
  testid,
  label,
  selectedLabel,
  onSelect,
}: {
  testid?: string;
  label: string;
  /** Display name to show when something is already chosen. */
  selectedLabel?: string | null;
  onSelect: (user: UserOption) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<string | null>(selectedLabel ?? null);
  const debounced = useDebouncedValue(query, 250);
  const results = useUsersSearch(debounced);
  const options = results.data?.users ?? [];

  function choose(u: UserOption) {
    setPicked(u.display_name);
    setQuery("");
    onSelect(u);
  }

  return (
    <div className="block text-xs" data-testid={testid}>
      <span className="text-slate-600">{label}</span>
      {picked && (
        <span
          data-testid={testid ? `${testid}-selected` : undefined}
          className="ms-2 rounded bg-brand-brass/10 px-1.5 py-0.5 font-medium text-brand-brassDark"
        >
          {picked}
        </span>
      )}
      <div className="relative mt-1">
        <input
          dir="auto"
          type="text"
          data-testid={testid ? `${testid}-input` : undefined}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("myWork.picker.placeholder")}
          className="block w-full rounded border border-slate-300 px-2 py-1 text-xs"
        />
        {debounced.trim().length >= 2 && options.length > 0 && (
          <ul
            data-testid={testid ? `${testid}-options` : undefined}
            className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded border border-slate-200 bg-white shadow-lg"
          >
            {options.map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  data-testid={testid ? `${testid}-option-${u.id}` : undefined}
                  onClick={() => choose(u)}
                  className="block w-full px-2 py-1 text-start hover:bg-slate-50"
                >
                  <span className="font-medium text-slate-800">{u.display_name}</span>
                  {u.email && <span className="ms-2 text-slate-400">{u.email}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
